import { Divider, Flex, Image, Spinner, Text, VStack } from '@chakra-ui/react';
import { useRouterState } from '@tanstack/react-router';
import { SIDEBAR_WIDTH } from '@/constants/dimensions';
import SidebarItem from './SidebarItem';
import SidebarGroup from './SidebarGroup';
import { useAuthContext } from '@/context/auth/useAuthContext';
import authMessages from '@/messages/auth';
import { adminMenu, isMenuGroup, memberCareRoutesWithLabels } from '@/constants/routes/app';
import { LogOut } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

const Sidebar = () => {
  const routerState = useRouterState();
  const pathName = routerState.location.pathname;

  const { signOut, isMemberCare, isSuperAdmin } = useAuthContext();

  const [isSigningOut, setIsSigningOut] = useState(false);

  // The home route ('/') must match exactly; every other route matches by prefix
  // so detail pages (e.g. /organizations/123) keep their parent item highlighted.
  const isRouteActive = (href: string) => (href === '/' ? pathName === '/' : pathName.startsWith(href));

  // Title of the group that contains the currently active route, if any.
  const activeGroupTitle = useMemo(() => {
    const group = adminMenu.find((entry) => isMenuGroup(entry) && entry.children.some((c) => isRouteActive(c.href)));
    return group?.title;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathName]);

  const [openGroups, setOpenGroups] = useState<string[]>(activeGroupTitle ? [activeGroupTitle] : []);

  // Keep the active group expanded as the route changes.
  useEffect(() => {
    if (activeGroupTitle) {
      setOpenGroups((prev) => (prev.includes(activeGroupTitle) ? prev : [...prev, activeGroupTitle]));
    }
  }, [activeGroupTitle]);

  const toggleGroup = (title: string) =>
    setOpenGroups((prev) => (prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]));

  // signOut navigates to /sign-in itself, before clearing state, and cannot throw
  // — so there is nothing to await around and nothing to recover from. The
  // spinner is not reset: this component unmounts with the sign-out.
  const handleSignOut = () => {
    if (isSigningOut) return;

    setIsSigningOut(true);
    signOut();
  };

  return (
    <Flex
      px={1}
      py={2}
      h="100vh"
      bgColor={'extra.darkWaterBlue'}
      w={SIDEBAR_WIDTH}
      flexDir="column"
      justifyContent="space-between"
    >
      <Flex px={1} flexDir="column" w="100%" h="100vh" alignItems={'center'}>
        <Flex mt={3} mb={1} align="center">
          <Image src="/img/logo/my-omnia-white-252x80.png" alt="MyOmnia Logo" width={'140px'} height={'45px'} />
        </Flex>

        <Divider marginY={3} borderColor={'whiteAlpha.300'} />

        {/* Sidebar items */}
        <VStack
          padding={0}
          margin={0}
          justifyContent={'space-between'}
          height={'100%'}
          width={'100%'}
          overflowY={'auto'}
        >
          <VStack gap={'2px'} padding={0} margin={0} width={'100%'} align={'stretch'}>
            {isSuperAdmin &&
              adminMenu.map((entry) => {
                if (isMenuGroup(entry)) {
                  const isGroupActive = entry.children.some((c) => isRouteActive(c.href));
                  return (
                    <SidebarGroup
                      key={entry.title}
                      title={entry.title}
                      icon={entry.icon}
                      isOpen={openGroups.includes(entry.title)}
                      isActive={isGroupActive}
                      onToggle={() => toggleGroup(entry.title)}
                    >
                      {entry.children.map((child) => (
                        <SidebarItem
                          key={child.title}
                          title={child.title}
                          href={child.href}
                          isSubItem
                          isSelected={isRouteActive(child.href)}
                        />
                      ))}
                    </SidebarGroup>
                  );
                }

                return (
                  <SidebarItem
                    key={entry.title}
                    title={entry.title}
                    href={entry.href}
                    icon={entry.icon}
                    isSelected={isRouteActive(entry.href)}
                  />
                );
              })}

            {isMemberCare &&
              memberCareRoutesWithLabels.map((item) => (
                <SidebarItem
                  key={item.title}
                  title={item.title}
                  href={item.href}
                  isSelected={isRouteActive(item.href)}
                />
              ))}
          </VStack>

          <VStack width={'100%'} align={'stretch'} gap={2}>
            <Divider borderColor={'whiteAlpha.300'} />
            <Flex
              as={'button'}
              onClick={handleSignOut}
              disabled={isSigningOut}
              cursor={isSigningOut ? 'not-allowed' : 'pointer'}
              alignItems={'center'}
              gap={'10px'}
              paddingX={'14px'}
              height={'44px'}
              fontWeight={600}
              fontSize={'15px'}
              color={'whiteAlpha.700'}
              bgColor={'transparent'}
              _hover={isSigningOut ? {} : { bgColor: 'rgba(216, 69, 69, 0.25)', color: 'extra.veryLightRed' }}
              borderRadius={'10px'}
              transition={'background-color 0.18s ease, color 0.18s ease'}
            >
              <LogOut size={18} strokeWidth={2} />
              {isSigningOut ? <Spinner size={'sm'} color={'text.mediumGray'} /> : <Text>{authMessages.signOut}</Text>}
            </Flex>
          </VStack>
        </VStack>
      </Flex>
    </Flex>
  );
};

export default Sidebar;
